import React, { Component, ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkLoadError: boolean;
  retryCount: number;
}

export class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isChunkLoadError: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a chunk load error
    const isChunkLoadError = 
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('ChunkLoadError') ||
      error.message.includes('Failed to import');

    return {
      hasError: true,
      error,
      isChunkLoadError,
      retryCount: 0
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ChunkLoadErrorBoundary caught an error:', error, errorInfo);
    
    // Log to error tracking service if available
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false,
        error_type: this.state.isChunkLoadError ? 'chunk_load_error' : 'runtime_error'
      });
    }
  }

  handleReload = () => {
    // Clear cache and reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.reload();
  };

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      isChunkLoadError: false,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkLoadError) {
        return (
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="max-w-md w-full space-y-4">
              <Alert className="border-orange-200 bg-orange-50">
                <AlertCircle className="h-4 w-4 text-orange-600" />
                <AlertTitle className="text-orange-800">
                  Page Refresh Required
                </AlertTitle>
                <AlertDescription className="text-orange-700">
                  <div className="space-y-3">
                    <p>
                      Some resources failed to load. This usually resolves itself with a quick refresh.
                    </p>
                    <p className="text-sm">
                      Please refresh the page to continue.
                    </p>
                  </div>
                </AlertDescription>
              </Alert>
              
              <div className="flex gap-3 justify-center">
                <Button 
                  onClick={this.handleReload}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh Page
                </Button>
                
                {this.state.retryCount < 3 && (
                  <Button 
                    variant="outline"
                    onClick={this.handleRetry}
                  >
                    Try Again
                  </Button>
                )}
              </div>

              <p className="text-xs text-muted-foreground text-center">
                If this problem persists, please try clearing your browser cache 
                or contact support.
              </p>
            </div>
          </div>
        );
      }

      // Generic error fallback
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full space-y-4">
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertTitle className="text-red-800">
                {this.props.fallbackMessage || 'Something went wrong'}
              </AlertTitle>
              <AlertDescription className="text-red-700">
                <div className="space-y-2">
                  <p>We're sorry, but an unexpected error occurred.</p>
                  {process.env.NODE_ENV === 'development' && this.state.error && (
                    <pre className="text-xs mt-2 p-2 bg-red-100 rounded overflow-auto">
                      {this.state.error.toString()}
                    </pre>
                  )}
                </div>
              </AlertDescription>
            </Alert>
            
            <div className="flex gap-3 justify-center">
              <Button 
                onClick={this.handleReload}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Reload Page
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}